<template>
  <v-container class="py-12">
    <v-row>
      <v-col :cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">Estoque de Peças</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs"></v-breadcrumbs>
          </div>
          <div class="button-group">
            <v-btn color="primary" elevation="0" to="/manutencoes/estoque/novo">
              <v-icon left small>mdi-plus</v-icon>
              Nova peça
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Cards de resumo -->
    <div v-if="items.length" class="stat-grid mb-1 px-3">
      <stat-card label="Total de itens" :value="total" color="#1450b5" />
      <stat-card label="Itens abaixo do mínimo" :value="lowStockCount" :color="lowStockCount > 0 ? '#d32f2f' : '#2e7d32'"
        :alert="lowStockCount > 0" />
      <stat-card label="Valor total do estoque" :value="formatCurrency(totalStockValue)" />
    </div>

    <!-- Tabela de peças -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text>
          <v-data-table
            :headers="headers"
            :items="items"
            :loading="loading"
            :server-items-length="total"
            :items-per-page="pagination.limit"
            @update:page="onPage"
            @update:items-per-page="onLimit"
            :footer-props="footerProps"
            no-data-text="Nenhuma peça cadastrada"
            loading-text="Carregando..."
            class="app-table hover-table"
          >
            <template v-slot:[`item.name`]="{ item }">
              <div class="font-weight-medium">{{ item.name }}</div>
              <div class="caption grey--text" v-if="item.sku">SKU: {{ item.sku }}</div>
            </template>
            <template v-slot:[`item.current_quantity`]="{ item }">
              <v-chip
                x-small
                :color="item.current_quantity <= item.minimum_quantity ? 'red' : 'green'"
                dark
              >
                {{ item.current_quantity }}
              </v-chip>
              <span class="caption grey--text ml-1">/ mín. {{ item.minimum_quantity }}</span>
            </template>
            <template v-slot:[`item.average_cost`]="{ item }">
              <span class="font-weight-medium">{{ formatCurrency(item.average_cost) }}</span>
            </template>
            <template v-slot:[`item.stock_value`]="{ item }">
              {{ formatCurrency((item.current_quantity || 0) * (item.average_cost || 0)) }}
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="$router.push('/manutencoes/estoque/' + item.id)">
                    <v-icon small>mdi-pencil-outline</v-icon>
                  </v-btn>
                </template>
                <span>Editar</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="remove(item)">
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>Remover</span>
              </v-tooltip>
            </template>
          </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Movimentações -->
    <v-row>
      <v-col cols="12">
        <v-card class="section-card">
          <v-card-title><div>Últimas movimentações<small>Entradas e saídas de peças</small></div></v-card-title>
          <v-card-text>
          <v-data-table
            class="app-table"
            :headers="movementHeaders"
            :items="movements"
            :loading="movementsLoading"
            :items-per-page="10"
            hide-default-footer
            no-data-text="Nenhuma movimentação registrada"
            loading-text="Carregando..."
          >
            <template v-slot:[`item.created_at`]="{ item }">
              {{ item.created_at | defaultDate('DD/MM/YYYY HH:mm') }}
            </template>
            <template v-slot:[`item.type`]="{ item }">
              <v-chip x-small :color="getMovementColor(item.type)" dark>
                <v-icon x-small left>{{ getMovementIcon(item.type) }}</v-icon>
                {{ getMovementLabel(item.type) }}
              </v-chip>
            </template>
            <template v-slot:[`item.origin`]="{ item }">
              {{ getOriginLabel(item.origin) }}
            </template>
            <template v-slot:[`item.quantity`]="{ item }">
              <span :class="item.type === 'in' ? 'green--text' : item.type === 'out' ? 'red--text' : ''">
                {{ item.type === 'in' ? '+' : item.type === 'out' ? '-' : '' }}{{ item.quantity }}
              </span>
            </template>
          </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'

const formatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

export default {
  name: 'PartStockList',
  data: () => ({
    breadcrumbs: [
      { text: 'Home', to: '/home', disabled: false, exact: true },
      { text: 'Manutenções', disabled: false, to: '/manutencoes', exact: true },
      { text: 'Estoque', disabled: true, exact: true },
    ],
    headers: [
      { text: 'Peça', value: 'name' },
      { text: 'Unidade', value: 'unit' },
      { text: 'Local', value: 'location' },
      { text: 'Estoque', value: 'current_quantity' },
      { text: 'Custo médio', value: 'average_cost' },
      { text: 'Valor estoque', value: 'stock_value', sortable: false },
      { text: 'Ações', value: 'actions', sortable: false, width: '100px' },
    ],
    movementHeaders: [
      { text: 'Data', value: 'created_at' },
      { text: 'Peça', value: 'part_name' },
      { text: 'Tipo', value: 'type' },
      { text: 'Origem', value: 'origin' },
      { text: 'Qtd', value: 'quantity' },
      { text: 'Saldo', value: 'balance_after' },
      { text: 'Placa', value: 'plate' },
    ],
    footerProps: {
      itemsPerPageText: 'Itens por página:',
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      pageText: '{0}-{1} de {2}',
    },
    items: [],
    total: 0,
    pagination: { page: 1, limit: 10 },
    loading: false,
    movementsLoading: false,
    movements: [],
  }),
  computed: {
    lowStockCount() {
      return this.items.filter((i) => i.current_quantity <= i.minimum_quantity).length
    },
    totalStockValue() {
      return this.items.reduce((sum, i) => sum + ((i.current_quantity || 0) * (i.average_cost || 0)), 0)
    },
  },
  created() {
    this.load()
    this.loadMovements()
  },
  methods: {
    formatCurrency(value) {
      return formatter.format(Number(value || 0))
    },
    getMovementColor(type) {
      const map = { in: 'green', out: 'orange', adjustment: 'blue-grey' }
      return map[type] || 'grey'
    },
    getMovementIcon(type) {
      const map = { in: 'mdi-arrow-down', out: 'mdi-arrow-up', adjustment: 'mdi-swap-horizontal' }
      return map[type] || 'mdi-circle-small'
    },
    getMovementLabel(type) {
      const map = { in: 'Entrada', out: 'Saída', adjustment: 'Ajuste' }
      return map[type] || type
    },
    getOriginLabel(origin) {
      const map = { manual: 'Manual', maintenance: 'Manutenção', inventory: 'Inventário' }
      return map[origin] || origin
    },
    notifyError(message) {
      if (this.$dialog?.notify) {
        this.$dialog.notify.error(message, { position: 'top-right', timeout: 5000 })
      }
    },
    notifySuccess(message) {
      if (this.$dialog?.notify) {
        this.$dialog.notify.success(message, { position: 'top-right', timeout: 4000 })
      }
    },
    async load() {
      this.loading = true
      try {
        const { data } = await axios.get('/api/v2/maintenance/part-stocks', {
          params: { page: this.pagination.page, limit: this.pagination.limit },
        })

        const content = data?.content || {}
        this.items = content.data || []
        this.total = content.total || 0
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : 'Erro ao carregar estoque.')
      } finally {
        this.loading = false
      }
    },
    async loadMovements() {
      this.movementsLoading = true
      try {
        const { data } = await axios.get('/api/v2/maintenance/part-stocks/movements', {
          params: { page: 1, limit: 10 },
        })
        this.movements = data?.content?.data || []
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : 'Erro ao carregar movimentações.')
      } finally {
        this.movementsLoading = false
      }
    },
    onPage(page) {
      this.pagination.page = page
      this.load()
    },
    onLimit(limit) {
      this.pagination.limit = limit
      this.pagination.page = 1
      this.load()
    },
    async remove(item) {
      if (!window.confirm('Deseja remover esta peça do estoque?')) return

      try {
        await axios.delete(`/api/v2/maintenance/part-stocks/${item.id}`)
        this.notifySuccess('Peça removida.')
        this.load()
        this.loadMovements()
      } catch (error) {
        this.notifyError(this.$codes ? this.$codes(error) : 'Erro ao remover peça.')
      }
    },
  },
}
</script>
